import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiArrowLeft, FiAlertCircle, FiDollarSign, FiCheckCircle } from 'react-icons/fi';
import { motion } from 'framer-motion';

const RefundPolicy = () => {
  const effectiveDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Deposit and Refund Policy | Paradise Performance LLC</title>
        <meta name="description" content="Review our deposit and refund policies for vehicle, golf cart and trailer purchases at Paradise Performance LLC in Paradise, TX." />
      </Helmet>

      {/* Header */}
      <div className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4">
          <Link 
            to="/" 
            className="inline-flex items-center text-gray-300 hover:text-white mb-4 transition-colors"
          >
            <FiArrowLeft className="mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold">Deposit and Refund Policy</h1>
          <p className="text-gray-300 mt-2">Effective Date: {effectiveDate}</p>
        </div>
      </div>

      {/* Company Info */}
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-600 font-medium">Company:</p>
              <p className="text-gray-900">Paradise Performance LLC</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">Website:</p>
              <p className="text-gray-900">https://paradise-performance.com</p>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border-l-4 border-red-500 p-6 mb-8"
        >
          <div className="flex items-start">
            <FiAlertCircle className="text-red-500 text-2xl mr-3 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-bold text-red-900 mb-2">Important Deposit Information</h3>
              <p className="text-red-800">
                <strong>All deposits are <span className="text-red-900">non-refundable</span></strong>, except where prohibited by law. 
                By placing a deposit, you acknowledge and agree to these terms. Please review this entire 
                policy carefully before making any purchase or deposit.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Policy Sections */}
        <div className="space-y-8">
          {/* Section 1: Deposits and Payments */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-md p-6"
          >
            <div className="flex items-center mb-4">
              <FiDollarSign className="text-primary-600 text-2xl mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">1. Deposits and Payments</h2>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span>All vehicle, trailer, and golf cart purchases may require a deposit at the time of order.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span className="font-semibold">
                  Deposits are NON-REFUNDABLE, except where prohibited by law.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span>
                  Deposits serve to secure your vehicle and compensate for administrative costs, 
                  scheduling, and lost sales opportunities.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span className="font-semibold">
                  Refunds will <strong>not</strong> be issued for any deposits or purchases, except where prohibited by law.
                </span>
              </li>
            </ul>
          </motion.section>

          {/* Section 2: Refunds and Returns */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-md p-6"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              2. Refunds and Returns (Vehicles, Golf Carts, and Trailers)
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span className="font-semibold"><strong>We do not refund any deposits</strong>, except where prohibited by law.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span>All vehicle, golf cart and trailer sales are considered final once the buyer takes possession.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span>
                  Returns are not accepted after the buyer takes possession, except in cases of material 
                  misrepresentation or where otherwise required by law.
                </span>
              </li>
            </ul>
          </motion.section>

          {/* Section 3: Vehicle Inspection at Pickup */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-md p-6"
          >
            <div className="flex items-center mb-4">
              <FiCheckCircle className="text-primary-600 text-2xl mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">3. Vehicle Inspection at Pickup</h2>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span>
                  All vehicles, golf carts and trailers are available for in-person inspection at our 
                  dealership located at 305 Co Rd 3470, Paradise, TX 76073.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span>
                  Buyers are encouraged to inspect the vehicle thoroughly before completing the purchase 
                  and taking possession.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span>
                  Any concerns about the condition of a vehicle must be raised before taking possession. 
                  Claims submitted after pickup are handled at the sole discretion of Paradise Performance LLC.
                </span>
              </li>
            </ul>
          </motion.section>

          {/* Section 4: Legal Disclaimer */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gray-100 rounded-lg p-6"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Legal Disclaimer</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-gray-600 mr-2">•</span>
                <span>This policy is governed by the laws of the State of Texas.</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-600 mr-2">•</span>
                <span>
                  Paradise Performance LLC reserves the right to update or modify this policy at any 
                  time without prior notice.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-600 mr-2">•</span>
                <span>Customers are encouraged to review this page regularly for updates.</span>
              </li>
            </ul>
          </motion.section>
        </div>

        {/* Contact Information */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-primary-50 rounded-lg p-6 mt-8"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-3">Questions About This Policy?</h3>
          <p className="text-gray-700 mb-4">
            If you have any questions about our deposit and refund policies, please contact us:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="font-semibold text-gray-900">Phone:</p>
              <a href="tel:9406832335" className="text-primary-600 hover:text-primary-700">
                (817) 382-2316
              </a>
            </div>
            <div>
              <p className="font-semibold text-gray-900">Address:</p>
              <p className="text-gray-700">
                305 Co Rd 3470<br />
                Paradise, TX 76073
              </p>
            </div>
          </div>
        </motion.div>

        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm">
          <Link to="/terms" className="text-primary-600 hover:text-primary-700">
            Terms of Service
          </Link>
          <span className="text-gray-400">|</span>
          <Link to="/privacy" className="text-primary-600 hover:text-primary-700">
            Privacy Policy
          </Link>
          <span className="text-gray-400">|</span>
          <Link to="/contact" className="text-primary-600 hover:text-primary-700">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
